import React from 'react';
import { Box, Typography } from '@mui/material';
import config from '../config';
import { useAuth } from '../context/AuthContext';

export type CompanyBrandVariant = 'drawer' | 'appbar';

// Logo box per place: the phone drawer's header (on the black drawer the
// logo sits on a white tile so dark logos stay visible), or the desktop
// header's size (40 x 180, as the desktop AppBar draws it today).
const SIZES: Record<CompanyBrandVariant, { height: number; maxWidth: number }> = {
  drawer: { height: 32, maxWidth: 160 },
  appbar: { height: 40, maxWidth: 180 },
};

/**
 * The signed-in user's company: its logo, or its name (one line with an
 * ellipsis) when it has no logo or the logo fails to load, so a broken
 * image never shows its alt text instead.
 */
export const CompanyBrand: React.FC<{ variant: CompanyBrandVariant }> = ({ variant }) => {
  const { user } = useAuth();
  const src = user?.CompanyLogo && user.CompanyID
    ? `${config.apiBaseUrl}/files/company-logos/${user.CompanyID}/${user.CompanyLogo}`
    : null;
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => setFailed(false), [src]);

  if (!user) return null;
  const name = user.CompanyName || '';
  const size = SIZES[variant];

  if (src && !failed) {
    const img = (
      <img
        src={src}
        alt={name || 'Company'}
        onError={() => setFailed(true)}
        style={{ display: 'block', height: size.height, maxWidth: size.maxWidth, objectFit: 'contain' }}
      />
    );
    if (variant === 'appbar') return img;
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          maxWidth: '100%',
          px: 1,
          py: 0.5,
          borderRadius: 1,
          backgroundColor: 'common.white',
        }}
      >
        {img}
      </Box>
    );
  }

  if (!name) return null;
  return variant === 'appbar' ? (
    <Typography variant="h6" noWrap sx={{ color: '#333', fontWeight: 600, fontSize: '1.1rem', maxWidth: size.maxWidth }}>
      {name}
    </Typography>
  ) : (
    <Typography
      variant="body2"
      noWrap
      sx={{ color: 'common.white', fontWeight: 600, fontSize: '0.95rem', lineHeight: 1.3, maxWidth: '100%' }}
    >
      {name}
    </Typography>
  );
};

export default CompanyBrand;
